/**
 * useCompanyLookup — reusable company lookup pipeline.
 *
 * Accepts any free-text query (company name, website URL, or domain),
 * resolves it to one or more candidate companies via the AI lookup endpoint,
 * and (optionally) runs the streaming enrichment for a chosen candidate.
 *
 * Two-step flow:
 *   1. lookup(query)         → /organizations/company-lookup
 *   2. selectCandidate(domain) → /organizations/enrich-domain (SSE)
 *
 * The hook auto-runs enrichment only when the lookup returns a single
 * *definite* match — a candidate parsed straight from a domain/URL
 * (classified_as === 'domain') or returned with high confidence — so simple
 * "I typed a domain" flows feel like one step. Anything less certain (a lone
 * medium/low-confidence name match, or multiple candidates) stops at
 * 'awaiting_choice' so the caller can present a "Did you mean?" picker.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useDomainEnrichment } from './useDomainEnrichment';
import type {
  CompanyCandidate,
  CompanyLookupResult,
  DomainEnrichmentResult,
} from '../../modules/master_data/contacts/types';

const LOOKUP_URL = '/api/v1/master-data/contacts/organizations/company-lookup';

export type LookupStep =
  | 'idle'
  | 'resolving'
  | 'awaiting_choice'
  | 'enriching'
  | 'done'
  | 'error';

export interface UseCompanyLookupReturn {
  step: LookupStep;
  candidates: CompanyCandidate[];
  selectedDomain: string | null;
  enrichment: DomainEnrichmentResult | null;
  /** Stream progress message (during enrichment) */
  progressMessage: string | null;
  error: string | null;
  isLoading: boolean;
  /** Start the lookup from a free-text query */
  lookup: (query: string) => Promise<void>;
  /** Pick one candidate from the list (used when multiple matches) */
  selectCandidate: (domain: string) => Promise<void>;
  reset: () => void;
}

export function useCompanyLookup(): UseCompanyLookupReturn {
  const enrichStream = useDomainEnrichment();
  const [step, setStep] = useState<LookupStep>('idle');
  const [candidates, setCandidates] = useState<CompanyCandidate[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const runEnrichment = useCallback(
    async (domain: string, seq: number) => {
      setSelectedDomain(domain);
      setStep('enriching');
      await enrichStream.enrich(domain);
      // The effect below transitions to "done" once data lands.
      if (seq !== seqRef.current) return;
    },
    [enrichStream],
  );

  const lookup = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) return;

      const seq = ++seqRef.current;
      enrichStream.reset();
      setError(null);
      setCandidates([]);
      setSelectedDomain(null);
      setStep('resolving');

      try {
        const response = await axiosInstance.post(LOOKUP_URL, { query: trimmed });
        if (seq !== seqRef.current) return;
        const result: CompanyLookupResult = response.data.data;

        if (!result.candidates || result.candidates.length === 0) {
          setStep('error');
          setError('NO_MATCHES');
          return;
        }

        setCandidates(result.candidates);

        // Only skip the picker and auto-enrich when the match is *definite*:
        // a single candidate that was either parsed straight from a domain/URL
        // (no AI guessing) or returned with high confidence. Anything less
        // certain — a lone medium/low-confidence name match, or several
        // candidates — surfaces the "did you mean?" choice so the user confirms.
        const onlyCandidate = result.candidates[0];
        const isDefiniteMatch =
          result.candidates.length === 1 &&
          // An org that already exists in the tenant is never auto-enriched: we
          // surface it so the user can open it instead of creating a duplicate.
          !onlyCandidate.existing_organization_id &&
          (result.classified_as === 'domain' || onlyCandidate.confidence === 'high');

        if (isDefiniteMatch) {
          await runEnrichment(onlyCandidate.domain, seq);
        } else {
          setStep('awaiting_choice');
        }
      } catch (err) {
        if (seq !== seqRef.current) return;
        setStep('error');
        setError(err instanceof Error ? err.message : 'LOOKUP_FAILED');
      }
    },
    [enrichStream, runEnrichment],
  );

  const selectCandidate = useCallback(
    async (domain: string) => {
      const seq = ++seqRef.current;
      await runEnrichment(domain, seq);
    },
    [runEnrichment],
  );

  const reset = useCallback(() => {
    seqRef.current++;
    enrichStream.reset();
    setStep('idle');
    setCandidates([]);
    setSelectedDomain(null);
    setError(null);
  }, [enrichStream]);

  // Transition to "done" when stream data arrives.
  useEffect(() => {
    if (step === 'enriching' && enrichStream.enrichment) {
      setStep('done');
    }
  }, [enrichStream.enrichment, step]);

  // Surface stream errors.
  useEffect(() => {
    if (step === 'enriching' && enrichStream.error) {
      setStep('error');
      setError(enrichStream.error);
    }
  }, [enrichStream.error, step]);

  return {
    step,
    candidates,
    selectedDomain,
    enrichment: enrichStream.enrichment,
    progressMessage: enrichStream.progressMessage,
    error,
    isLoading: step === 'resolving' || step === 'enriching',
    lookup,
    selectCandidate,
    reset,
  };
}
